import { type } from "os";

export class MultipleLanguages {
	en?: string;
	zh?: string;
	km?: string;
}

export type SubscriptionPrice = {
	quotation?: number; // months
	price: number;
	discount?: number;
};

export enum BENEFIT_CODE {
	CAR_PORT = "carPost", // number new car posts per month
	HOT_SALE = "hotSale", // [Y] number hot sales in [Z] number days per month
	CHAT_WITH_PROSPECTS = "chatWithProspects", // Chat directly with prospect customers
	COMMUNITY = "Community", // Can post on community
	REQUEST_AUCTION = "requestAuction", // Request to create auction
	CHECK_CAR = "checkCar", //Request for car checks
	UNLIMITED_POST_TOP_DEAL = "unlimitedPostTopDeal", // Unlimited post top deal
	SALE_SUPPORT = "saleSupport", // Sale support
	CUSTOMER_SERVICE_SUPPORT = "customerServiceSupport", // Customer service support
	DIGITAL_MEDIA_SUPPORT = "digitalMediaSupport", // Digital media support
}

export enum BENEFIT_TYPE {
	MAIN = "main",
	OTHER = "other",
}

export type CommissionConfig = {
	fee?: number;
	percent?: number;
	start?: number;
	end?: number;
};

export type SubscriptionBenefit = {
	code: BENEFIT_CODE;
	type: BENEFIT_TYPE;
	hidden?: boolean;
	checked?: boolean;
	unlimited?: boolean;
	quota?: number;
	duration?: number; // days
};

export class BaseModelSQLType {
	id?: number;
	status?: string;
	updatedAt?: Date;
	createdAt?: Date;
	constructor(data?: any) {
		if (data) {
			this.id = data.id;
			this.status = data.status;
			this.updatedAt = data.updatedAt;
			this.createdAt = data.createdAt;
		}
	}
}
export enum PACKAGE_STATUS {
	ENABLED = "enabled",
	DISABLED = "disabled",
}
export class SubscriptionPackage extends BaseModelSQLType {
	name?: MultipleLanguages;
	description?: MultipleLanguages;
	prices?: SubscriptionPrice[];
	benefits?: SubscriptionBenefit[];
	updatedBy?: any;
	status?: PACKAGE_STATUS;
	isEnabled?: boolean;
	isBestSeller?: boolean;
	icon?: string;
	iconUrl?: string;
	type?: string;
	ordinal?: number;
	annualMembership?: number;
	commissions?: CommissionConfig[];

	constructor(data?: Partial<SubscriptionPackage>) {
		super(data);
		if (data) {
			this.name = data.name;
			this.description = data.description;
			this.status = data.status;
			this.prices = data.prices;
			this.benefits = data.benefits?.map(el => {
				el.checked = !el.hidden;
				el.unlimited = el.unlimited ?? false;
				return el;
			});
			this.updatedBy = data.updatedBy;
			this.isBestSeller = data.isBestSeller;
			this.icon = data.icon;
			this.iconUrl = data.iconUrl;
			this.type = data.type;
			this.ordinal = data.ordinal;
			this.annualMembership = data.annualMembership;
			this.commissions = data.commissions;
			this.isEnabled = data.status === PACKAGE_STATUS.ENABLED;
		}
	}
	static serialize(data: SubscriptionPackage) {
		return {
			id: data.id,
			name: data.name,
			description: data.description,
			prices: data.prices,
			status: data.isEnabled ? PACKAGE_STATUS.ENABLED : PACKAGE_STATUS.DISABLED,
			benefits: data.benefits?.map(el => {
				const data: SubscriptionBenefit = {
					code: el.code,
					type: el.type,
				};
				el.duration && (data.duration = el.duration);
				el.unlimited && (data.unlimited = true);
				!data.unlimited && (data.quota = el.quota);
				data.hidden = !el.checked;
				return data;
			}),
			updatedBy: data.updatedBy,
			isBestSeller: data.isBestSeller,
			icon: data.icon,
			type: data.type,
			ordinal: data.ordinal,
			commissions: data.commissions?.map(el => {
				if (el.start && !el.end) {
					delete el.end;
				}
				return el;
			}),
		};
	}
}

export interface SubscriptionPackageParams {
	status?: string;
	skip: number;
	take: number;
}

export type SubscriptionBenefitConfig = {
	limit?: boolean;
	quota?: boolean;
	checked?: boolean;
	duration?: boolean;
	type: BENEFIT_TYPE;
	code: BENEFIT_CODE;
};
export const SubscriptionBenefitConfigs: {
	[key: string]: SubscriptionBenefitConfig;
} = {
	[BENEFIT_CODE.CAR_PORT]: {
		limit: true,
		quota: true,
		type: BENEFIT_TYPE.MAIN,
		code: BENEFIT_CODE.CAR_PORT,
	},
	[BENEFIT_CODE.HOT_SALE]: {
		quota: true,
		duration: true,
		type: BENEFIT_TYPE.MAIN,
		code: BENEFIT_CODE.HOT_SALE,
	},
	[BENEFIT_CODE.CHAT_WITH_PROSPECTS]: {
		type: BENEFIT_TYPE.OTHER,
		code: BENEFIT_CODE.CHAT_WITH_PROSPECTS,
	},
	[BENEFIT_CODE.CHECK_CAR]: {
		type: BENEFIT_TYPE.OTHER,
		code: BENEFIT_CODE.CHECK_CAR,
	},
	[BENEFIT_CODE.REQUEST_AUCTION]: {
		type: BENEFIT_TYPE.OTHER,
		code: BENEFIT_CODE.REQUEST_AUCTION,
	},
	[BENEFIT_CODE.COMMUNITY]: {
		type: BENEFIT_TYPE.OTHER,
		code: BENEFIT_CODE.COMMUNITY,
	},
	[BENEFIT_CODE.UNLIMITED_POST_TOP_DEAL]: {
		type: BENEFIT_TYPE.OTHER,
		code: BENEFIT_CODE.UNLIMITED_POST_TOP_DEAL,
	},
	[BENEFIT_CODE.SALE_SUPPORT]: {
		type: BENEFIT_TYPE.OTHER,
		code: BENEFIT_CODE.SALE_SUPPORT,
	},
	[BENEFIT_CODE.CUSTOMER_SERVICE_SUPPORT]: {
		type: BENEFIT_TYPE.OTHER,
		code: BENEFIT_CODE.CUSTOMER_SERVICE_SUPPORT,
	},
	[BENEFIT_CODE.DIGITAL_MEDIA_SUPPORT]: {
		type: BENEFIT_TYPE.OTHER,
		code: BENEFIT_CODE.DIGITAL_MEDIA_SUPPORT,
	},
};
